import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import DownloadIcon from '@mui/icons-material/Download';
import { ChatAnexo, tamanhoLegivel } from "./chatTypes";
import { carregarImagemAnexo, urlImagemCache } from "./imagemAnexo";

type Props = {
    anexo: ChatAnexo
    onVisualizar: (anexo: ChatAnexo) => void
    onBaixar: (anexo: ChatAnexo) => void
};

// Miniatura da imagem dentro do balão (estilo WhatsApp): mostra sem baixar,
// clique abre o visualizador e o botão de download continua disponível.
function ChatAnexoImagem({ anexo, onVisualizar, onBaixar }: Props) {
    const cacheada = urlImagemCache(anexo.id_chat_anexo);
    const [url, setUrl] = useState<string | null>(cacheada);
    const [erro, setErro] = useState(false);
    const [visivel, setVisivel] = useState(Boolean(cacheada));
    const caixaRef = useRef<HTMLDivElement | null>(null);

    // Só baixa quando o balão chega perto da tela: num histórico longo, evita
    // puxar todas as fotos de uma vez.
    useEffect(() => {
        if (visivel) return;
        const alvo = caixaRef.current;
        if (!alvo) return;
        if (typeof IntersectionObserver === 'undefined') {
            setVisivel(true);
            return;
        }

        const observer = new IntersectionObserver(entradas => {
            if (entradas.some(e => e.isIntersecting)) {
                setVisivel(true);
                observer.disconnect();
            }
        }, { rootMargin: '200px' });

        observer.observe(alvo);
        return () => observer.disconnect();
    }, [visivel]);

    useEffect(() => {
        if (!visivel || url) return;
        let ativo = true;
        carregarImagemAnexo(anexo.id_chat_anexo, anexo.mime_type)
            .then(u => { if (ativo) setUrl(u); })
            .catch(() => { if (ativo) setErro(true); });
        return () => { ativo = false; };
    }, [visivel, url, anexo.id_chat_anexo, anexo.mime_type]);

    const handleBaixar = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        onBaixar(anexo);
    }, [anexo, onBaixar]);

    // Não carregou (arquivo removido do disco, sem permissão): cai no formato de arquivo
    if (erro) {
        return (
            <Box
                onClick={() => onBaixar(anexo)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1, p: 0.8, borderRadius: 1,
                    bgcolor: 'rgba(0,0,0,0.04)', cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                }}
            >
                <BrokenImageIcon sx={{ fontSize: 20, color: '#9e9e9e' }} />
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {anexo.nome_original}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: '#757575' }}>
                        Não foi possível exibir — clique para baixar
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            ref={caixaRef}
            onClick={() => url && onVisualizar(anexo)}
            sx={{
                position: 'relative',
                width: 220,
                maxWidth: '100%',
                minHeight: url ? 0 : 120,
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'rgba(0,0,0,0.04)',
                cursor: url ? 'pointer' : 'default',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover .chat-img-baixar': { opacity: 1 },
            }}
        >
            {url ? (
                <img
                    src={url}
                    alt={anexo.nome_original}
                    onError={() => setErro(true)}
                    style={{ display: 'block', width: '100%', maxHeight: 280, objectFit: 'cover' }}
                />
            ) : (
                <CircularProgress size={22} sx={{ color: '#bdbdbd' }} />
            )}

            <Tooltip title="Baixar imagem">
                <IconButton
                    className="chat-img-baixar"
                    size="small"
                    onClick={handleBaixar}
                    sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        p: 0.4,
                        bgcolor: 'rgba(0,0,0,0.55)',
                        color: '#fff',
                        opacity: { xs: 1, md: 0 },   // no celular não existe hover
                        transition: 'opacity .15s',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                    }}
                >
                    <DownloadIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Tooltip>

            {url && (
                <Typography
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        px: 0.8,
                        py: 0.3,
                        fontSize: 10,
                        color: '#fff',
                        bgcolor: 'rgba(0,0,0,0.45)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {anexo.nome_original} · {tamanhoLegivel(anexo.tamanho_bytes)}
                </Typography>
            )}
        </Box>
    );
}

export default React.memo(ChatAnexoImagem);
